import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  useModal,
  Modal,
  ModalVariant,
  AlloyButton
} from 'cat-ecommerce-alloy';
import { SIS_PARTS_LIST } from '@app/components/login/redirect/constants';
import { getCookie } from '@app/utils';
import ValidInvalidPartsModal from '../ValidInvalidPartsModal';
import { itemNumberValidator, quantityValidator } from '../../QuickOrder/utils';
import QuickOrderMobileModal from '../../QuickOrder/QuickOrderMobileModal';
import AddToCartModifications from '../../QuickOrder/AddToCartModifications';
import { JAPANESE_LANGUAGE_CODE, STATUS } from '@app/constants/commonConstants';
import cx from 'classnames';
import styles from './styles.module.scss';

const AddToCartModal = ({
  modalOpen,
  setModalOpen,
  parentModalProps,
  isModalTypeSis,
  errorInfo,
  initialItems,
  shoppingCartMaxItems,
  isAvailabilityToggled,
  setIsAvailabilityToggled,
  isMD,
  onAddToCartClick
}) => {
  const [t] = useTranslation();
  const { open, close, isOpen } = parentModalProps;
  const modalProps = useModal();
  const sisCookie = getCookie(SIS_PARTS_LIST) || '';
  const isInvalidParts = useSelector(state => !!state.checkout?.isInvalidParts);
  const addToCartStatus = useSelector(s => s.checkout.addToCartStatus);
  const addToCartLoading = addToCartStatus === STATUS.RESOLVED;
  const quickOrderMobileModalOptions = useModal();
  const title = t('Quick_Title');
  const replacedParts = useSelector(s => s.orders.replacedParts);
  const replacementHasReplacement = !!replacedParts?.length;

  const isPCC_SpecialCharactersValidationFlag = useSelector(
    s => s.featureFlag?.PCC_SpecialCharactersValidationFlag
  );
  const isPCC_ShoppingCartSingleByteMessageJapan = useSelector(
    s => s.featureFlag?.PCC_ShoppingCartSingleByteMessageJapan
  );
  const isDefaultLanguageJapanese = useSelector(
    s => s.dealer.defaultLanguage === JAPANESE_LANGUAGE_CODE
  );
  const singleByteRequiredErrorMessage = t('ERROR_SINGLE_BYTE_REQUIRED');

  useEffect(() => {
    if (
      isInvalidParts ||
      (!isInvalidParts && !isMD && sisCookie) ||
      replacementHasReplacement
    ) {
      open();
    }
  }, [isInvalidParts, isMD, replacementHasReplacement]);

  useEffect(() => {
    addToCartLoading && close();
  }, [addToCartLoading]);

  useEffect(() => {
    modalOpen && open();
  }, [modalOpen]);

  const modalTitle = isModalTypeSis
    ? t('SIS_TO_CART_MODAL1')
    : t('Quick_Title');

  const qOItemNumberValidator = props => {
    const params = {
      ...props,
      requiredErrorMessage: t('QUICKORDER_BLANK_ITEM'),
      invalidCharacterErrorMessage: isPCC_SpecialCharactersValidationFlag
        ? t('INVALID_ITEM_NUMBER')
        : t('INVALID_PART_NUMBER'),
      validateDoubleByte:
        isDefaultLanguageJapanese && isPCC_ShoppingCartSingleByteMessageJapan,
      specialCharacterValidation: isPCC_SpecialCharactersValidationFlag,
      doubleByteErrorMessage: singleByteRequiredErrorMessage
    };
    return itemNumberValidator(params);
  };
  const closeModal = () => {
    setModalOpen && setModalOpen(false);
    close();
  };

  return (
    <>
      <Modal
        title={modalTitle}
        isOpen={isOpen}
        variant={ModalVariant.large}
        close={modalProps.isOpen ? undefined : closeModal}
        portalTarget="react-root-modals"
      >
        {isModalTypeSis && isInvalidParts && (
          <ValidInvalidPartsModal modalOptions={quickOrderMobileModalOptions} />
        )}
        {!isModalTypeSis && !isMD && (
          <QuickOrderMobileModal
            childModalOptions={modalProps}
            errorInfo={errorInfo}
            initialItems={initialItems}
            itemNumberValidator={qOItemNumberValidator}
            maxRows={shoppingCartMaxItems}
            mobileModalOptions={parentModalProps}
            quantityValidator={quantityValidator}
            shoppingCartMaxItems={shoppingCartMaxItems}
            isAvailabilityToggled={isAvailabilityToggled}
            setIsAvailabilityToggled={setIsAvailabilityToggled}
            onAddToCartClick={onAddToCartClick}
          />
        )}
        <AddToCartModifications
          modalOptions={modalProps}
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
          isModalTypeSis={isModalTypeSis}
        />
      </Modal>
      {!isModalTypeSis && !isMD && (
        <div
          className={cx(
            'p-2 mb-3 d-flex flex-column d-print-none',
            styles['quick-order-mobile-container']
          )}
        >
          <p className="pb-1 mb-0 text-start text-xl text-uppercase text-univers--bold u-color--ExtraDarkGray">
            {title}
          </p>
          <hr className="w-100 my-2" />
          <p className="mb-0 pt-1 text-xs u-color--DarkGray">
            {t('QUICK_DESCRIPTION')}
          </p>
          <AlloyButton
            buttonType="tertiary"
            className="w-100 mt-2 pt-1"
            size="large"
            onClick={open}
          >
            {t('ADD_ITEMS')}
          </AlloyButton>
        </div>
      )}
    </>
  );
};
AddToCartModal.propTypes = {
  parentModalProps: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  }),
  errorInfo: PropTypes.shape({
    domain: PropTypes.string,
    message: PropTypes.string,
    path: PropTypes.string
  }),
  initialItems: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.string,
      itemNumber: PropTypes.string,
      assetId: PropTypes.string,
      lineItemNote: PropTypes.string,
      customerPartNumber: PropTypes.string,
      customerItemNumber: PropTypes.string,
      id: PropTypes.string
    })
  ),
  shoppingCartMaxItems: PropTypes.number,
  isAvailabilityToggled: PropTypes.bool,
  setIsAvailabilityToggled: PropTypes.func,
  isModalTypeSis: PropTypes.bool,
  isMD: PropTypes.bool,
  onAddToCartClick: PropTypes.func,
  modalOpen: PropTypes.bool,
  setModalOpen: PropTypes.func
};
export default AddToCartModal;
